import type { Deploy, Preview, RunPhase, RunSummary, SseClientEvent } from '@/api/models';
import { isTerminalPhase, phaseIndex, phaseLabel } from './phases';

/**
 * THE NEUTRAL PROGRESS PROJECTION
 * ===============================
 *
 * This is the piece the whole client run view is built on, and it is the piece
 * that does not fall out of the stream on its own.
 *
 * After scrubbing, the client stream carries only assistant text, asks, status
 * and deploy events. And in print mode the agent emits very little assistant
 * text mid-run - so a UI that waits for the agent to narrate what it is doing
 * shows a non-technical person a blank panel for two minutes while they wonder
 * whether they have broken their product.
 *
 * So the "plain-language phases" a client sees are **derived**, not reported.
 * They come from exactly five inputs, and never from agent chatter:
 *
 *   1. `run_started`   - opens a stop, at stage `queued`.
 *   2. `phase`         - the server's own neutral projection. Authoritative.
 *   3. `deploy`        - a deploy state change advances the stage on its own,
 *                        so the last third of the run stays truthful even if no
 *                        `phase` frame arrives for it. `queued`/`building` map
 *                        to "Publishing your preview", `verifying` to
 *                        "Checking it works", `succeeded` closes the stage.
 *   4. `error_code`    - records a neutral failure code against the stop.
 *   5. `run_finished` / `run_failed` - the only authority on the terminal glyph.
 *
 * Agent text (`message`, `text_delta`) is a **liveness signal only**. It moves
 * `lastSignalAt`, which the rail uses to show that something is happening, and
 * it may advance a stop that is still sitting at `queued`/`preparing` to
 * `working` - because text from the agent is proof work started. It can never
 * set any other stage and it never appears as a phase caption.
 *
 * Three invariants hold over every stop:
 *
 *   * **Monotonic.** A stage never moves backwards. Out-of-order or duplicated
 *     frames cannot make the ledger appear to lose its place.
 *   * **Measured, never estimated.** Every duration is an observed elapsed
 *     time. `progress` is carried only when the server sends a real fraction;
 *     there is no synthetic ticker anywhere.
 *   * **Terminal is sticky.** Once a run has finished or failed, later frames
 *     may enrich it (a preview URL landing late) but cannot reopen it.
 */

export type LedgerOutcome = 'running' | 'succeeded' | 'failed' | 'expired' | 'cancelled';

export type SegmentSource = 'phase' | 'deploy' | 'inferred' | 'seed';

export interface LedgerSegment {
  phase: RunPhase;
  label: string;
  /** ms epoch */
  enteredAt: number;
  /** ms epoch, or null while this is the live segment */
  endedAt: number | null;
  /** A real fraction from the server, or null. Never synthesised. */
  progress: number | null;
  source: SegmentSource;
}

export interface LedgerWaiting {
  askId: string;
  question: string;
  /** False when the ask belongs to the review phase and is not this client's to answer. */
  answerableByClient: boolean;
}

export interface LedgerStop {
  runId: number;
  /** The human turn that caused this run, in the client's visibility projection. */
  messageSeq: number | null;
  startedAt: number;
  endedAt: number | null;
  segments: LedgerSegment[];
  currentPhase: RunPhase;
  outcome: LedgerOutcome;
  waiting: LedgerWaiting | null;
  /** Last time any evidence of life arrived, including agent text. */
  lastSignalAt: number;
  errorCode: string | null;
  errorRetryable: boolean;
  preview: Preview | null;
  /** Latest state per deploy id, so a repeated frame updates rather than appends. */
  deploys: Record<string, Deploy>;
}

export type LedgerState = {
  /** Oldest first. The rail draws them in this order. */
  stops: LedgerStop[];
};

export const emptyLedger = (): LedgerState => ({ stops: [] });

const toMs = (iso: string | null | undefined, fallback: number): number => {
  if (!iso) return fallback;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? fallback : t;
};

function newStop(runId: number, at: number, messageSeq: number | null): LedgerStop {
  return {
    runId,
    messageSeq,
    startedAt: at,
    endedAt: null,
    segments: [{ phase: 'queued', label: phaseLabel('queued'), enteredAt: at, endedAt: null, progress: null, source: 'seed' }],
    currentPhase: 'queued',
    outcome: 'running',
    waiting: null,
    lastSignalAt: at,
    errorCode: null,
    errorRetryable: true,
    preview: null,
    deploys: {},
  };
}

/**
 * Move a stop forward to `phase`.
 *
 * Refuses to go backwards, refuses to reopen a terminal stop, and refuses to
 * re-enter the stage it is already in (which would otherwise split one stage
 * into a run of zero-length slivers every time a duplicate frame arrived).
 */
function advance(
  stop: LedgerStop,
  phase: RunPhase,
  at: number,
  source: SegmentSource,
  label?: string | null,
  progress?: number | null,
): LedgerStop {
  if (stop.outcome !== 'running') return stop;
  if (isTerminalPhase(phase)) return stop; // terminal is run_finished / run_failed's job

  const nextIndex = phaseIndex(phase);
  const currentIndex = phaseIndex(stop.currentPhase);

  if (nextIndex < currentIndex) return stop; // monotonic
  if (nextIndex === currentIndex) {
    // Same stage: only a real progress fraction or a better label is news.
    const last = stop.segments[stop.segments.length - 1];
    if (!last) return stop;
    const nextProgress = progress ?? last.progress;
    const nextLabel = label?.trim() ? label.trim() : last.label;
    if (nextProgress === last.progress && nextLabel === last.label) return stop;
    const segments = stop.segments.slice(0, -1);
    segments.push({ ...last, progress: nextProgress, label: nextLabel });
    return { ...stop, segments, lastSignalAt: Math.max(stop.lastSignalAt, at) };
  }

  const segments = stop.segments.slice();
  const last = segments[segments.length - 1];
  // Clamp: a clock skew that closes a segment before it opened would render a
  // negative-height bar on the rail.
  const closeAt = last ? Math.max(last.enteredAt, at) : at;
  if (last && last.endedAt === null) segments[segments.length - 1] = { ...last, endedAt: closeAt };

  segments.push({
    phase,
    label: phaseLabel(phase, label),
    enteredAt: closeAt,
    endedAt: null,
    progress: progress ?? null,
    source,
  });

  return { ...stop, segments, currentPhase: phase, lastSignalAt: Math.max(stop.lastSignalAt, at) };
}

function close(stop: LedgerStop, at: number, outcome: LedgerOutcome): LedgerStop {
  if (stop.outcome !== 'running') return stop;
  const segments = stop.segments.slice();
  const last = segments[segments.length - 1];
  const endAt = last ? Math.max(last.enteredAt, at) : at;
  if (last && last.endedAt === null) segments[segments.length - 1] = { ...last, endedAt: endAt };
  return {
    ...stop,
    segments,
    endedAt: endAt,
    outcome,
    currentPhase: outcome === 'succeeded' ? 'finished' : 'failed',
    waiting: null,
    lastSignalAt: Math.max(stop.lastSignalAt, at),
  };
}

/** Deploy status to the stage it implies. `succeeded` closes verifying rather than opening a stage. */
function phaseForDeploy(status: Deploy['status']): RunPhase | null {
  switch (status) {
    case 'queued':
    case 'building':
      return 'deploying';
    case 'verifying':
      return 'verifying';
    case 'succeeded':
      return 'verifying';
    case 'failed':
    case 'cancelled':
      return null;
    default:
      return null;
  }
}

interface ApplyResult {
  ledger: LedgerState;
  /** Set when this event carried a preview update the chat header should adopt. */
  preview?: Preview | null;
}

function replaceStop(state: LedgerState, index: number, stop: LedgerStop): LedgerState {
  const stops = state.stops.slice();
  stops[index] = stop;
  return { stops };
}

function findStop(state: LedgerState, runId: number): number {
  for (let i = state.stops.length - 1; i >= 0; i -= 1) {
    if (state.stops[i]!.runId === runId) return i;
  }
  return -1;
}

/**
 * Ensure a stop exists for `runId`.
 *
 * A resumed stream can deliver a `phase` frame for a run whose `run_started`
 * was replayed before the cursor we resumed from. Creating the stop implicitly
 * is what makes a mid-run reconnect show the run instead of dropping it.
 */
function ensureStop(state: LedgerState, runId: number, at: number, messageSeq: number | null): { state: LedgerState; index: number } {
  const existing = findStop(state, runId);
  if (existing >= 0) return { state, index: existing };
  const stops = state.stops.concat(newStop(runId, at, messageSeq));
  stops.sort((a, b) => a.startedAt - b.startedAt || a.runId - b.runId);
  return { state: { stops }, index: stops.findIndex((s) => s.runId === runId) };
}

/**
 * Apply one client stream event to the ledger.
 *
 * Pure and idempotent per cursor: the stream layer de-duplicates by cursor, and
 * everything here is either monotonic or a last-write-wins field, so a replayed
 * frame cannot corrupt a stop.
 */
export function applyEvent(state: LedgerState, event: SseClientEvent): ApplyResult {
  const now = Date.now();
  const at = toMs((event as { at?: string }).at, now);
  const runId = (event as { run_id?: number | null }).run_id ?? null;

  switch (event.kind) {
    case 'run_started': {
      const { state: s, index } = ensureStop(state, event.run_id, at, event.message_seq ?? null);
      const stop = s.stops[index]!;
      return {
        ledger: replaceStop(s, index, {
          ...stop,
          messageSeq: stop.messageSeq ?? event.message_seq ?? null,
          startedAt: Math.min(stop.startedAt, at),
        }),
      };
    }

    case 'phase': {
      if (runId === null) return { ledger: state };
      const { state: s, index } = ensureStop(state, runId, at, null);
      const stop = s.stops[index]!;

      if (event.phase === 'failed') return { ledger: replaceStop(s, index, close(stop, at, 'failed')) };
      if (event.phase === 'finished') return { ledger: replaceStop(s, index, close(stop, at, 'succeeded')) };

      return {
        ledger: replaceStop(s, index, advance(stop, event.phase, at, 'phase', event.label, event.progress ?? null)),
      };
    }

    case 'deploy': {
      const deploy = event.deploy;
      const preview = event.preview ?? null;
      if (!deploy) return { ledger: state, preview };

      // A deploy frame can arrive with no run attached (a redeploy triggered
      // outside a run). It still updates the preview; it just has no stop.
      if (runId === null) return { ledger: state, preview };

      const { state: s, index } = ensureStop(state, runId, at, null);
      let stop = s.stops[index]!;

      const deployKey = String(deploy.id);
      stop = {
        ...stop,
        deploys: { ...stop.deploys, [deployKey]: deploy },
        preview: preview ?? stop.preview,
        lastSignalAt: Math.max(stop.lastSignalAt, at),
      };

      const implied = phaseForDeploy(deploy.status);
      if (implied) stop = advance(stop, implied, at, 'deploy');

      if (deploy.status === 'failed' || deploy.status === 'cancelled') {
        stop = { ...stop, errorCode: stop.errorCode ?? 'deploy_failed', errorRetryable: true };
      }

      return { ledger: replaceStop(s, index, stop), preview };
    }

    case 'message':
    case 'text_delta': {
      // Liveness only. Agent chatter is never a phase - see the header comment.
      if (runId === null) return { ledger: state };
      const index = findStop(state, runId);
      if (index < 0) return { ledger: state };
      let stop = state.stops[index]!;
      if (stop.outcome !== 'running') return { ledger: state };

      stop = { ...stop, lastSignalAt: Math.max(stop.lastSignalAt, at) };
      // Text from the agent is proof the work started. Only ever forward, and
      // only ever as far as "Making the changes".
      if (phaseIndex(stop.currentPhase) < phaseIndex('working')) {
        stop = advance(stop, 'working', at, 'inferred');
      }
      return { ledger: replaceStop(state, index, stop) };
    }

    case 'ask': {
      if (runId === null) return { ledger: state };
      const { state: s, index } = ensureStop(state, runId, at, null);
      const stop = s.stops[index]!;
      const ask = event.ask;
      return {
        ledger: replaceStop(s, index, {
          ...stop,
          lastSignalAt: Math.max(stop.lastSignalAt, at),
          waiting: ask?.answered
            ? null
            : {
                askId: ask?.ask_id ?? '',
                question: ask?.question ?? '',
                answerableByClient: (event.answerable_by ?? 'client') === 'client',
              },
        }),
      };
    }

    case 'ask_resolved': {
      if (runId === null) return { ledger: state };
      const index = findStop(state, runId);
      if (index < 0) return { ledger: state };
      const stop = state.stops[index]!;
      if (stop.waiting && stop.waiting.askId !== event.ask_id) return { ledger: state };
      return {
        ledger: replaceStop(state, index, { ...stop, waiting: null, lastSignalAt: Math.max(stop.lastSignalAt, at) }),
      };
    }

    case 'error_code': {
      if (runId === null) return { ledger: state };
      const index = findStop(state, runId);
      if (index < 0) return { ledger: state };
      const stop = state.stops[index]!;
      return {
        ledger: replaceStop(state, index, {
          ...stop,
          errorCode: event.error_code,
          errorRetryable: event.retryable ?? true,
          lastSignalAt: Math.max(stop.lastSignalAt, at),
        }),
      };
    }

    case 'run_finished': {
      const { state: s, index } = ensureStop(state, event.run_id, at, null);
      const stop = s.stops[index]!;
      const outcome: LedgerOutcome =
        event.status === 'succeeded' ? 'succeeded' : event.status === 'cancelled' ? 'cancelled' : 'failed';
      return { ledger: replaceStop(s, index, close(stop, at, outcome)) };
    }

    case 'run_failed': {
      const { state: s, index } = ensureStop(state, event.run_id, at, null);
      let stop = s.stops[index]!;
      stop = { ...stop, errorCode: event.error_code ?? stop.errorCode };
      const outcome: LedgerOutcome = event.status === 'expired' ? 'expired' : 'failed';
      return { ledger: replaceStop(s, index, close(stop, at, outcome)) };
    }

    default:
      return { ledger: state };
  }
}

/**
 * Seed a stop from the chat fetch's `active_run`.
 *
 * A client who loads the page mid-run must see the run immediately, before any
 * frame arrives. The stream then takes over and refines it; because every
 * transition is monotonic, the seed can only ever be corrected forward.
 */
export function seedFromRunSummary(state: LedgerState, run: RunSummary | null | undefined, preview: Preview | null): LedgerState {
  if (!run || run.actor_kind !== 'client') return state;
  const startedAt = toMs(run.started_at, Date.now());
  const { state: s, index } = ensureStop(state, run.id, startedAt, null);
  let stop = s.stops[index]!;
  stop = { ...stop, preview: stop.preview ?? preview };

  if (run.phase && !isTerminalPhase(run.phase)) {
    stop = advance(stop, run.phase, Math.max(startedAt, stop.lastSignalAt), 'seed');
  }
  if (run.error_code) stop = { ...stop, errorCode: stop.errorCode ?? run.error_code };

  const finishedAt = toMs(run.finished_at, 0);
  if (run.status === 'succeeded') stop = close(stop, finishedAt || Date.now(), 'succeeded');
  else if (run.status === 'failed' || run.status === 'cancelled') stop = close(stop, finishedAt || Date.now(), run.status === 'cancelled' ? 'cancelled' : 'failed');
  else if (run.status === 'expired') stop = close(stop, finishedAt || Date.now(), 'expired');

  return replaceStop(s, index, stop);
}

// ---------------------------------------------------------------------------
// Read helpers used by the rail
// ---------------------------------------------------------------------------

export function segmentDuration(segment: LedgerSegment, now: number): number {
  return Math.max(0, (segment.endedAt ?? now) - segment.enteredAt);
}

export function stopDuration(stop: LedgerStop, now: number): number {
  return Math.max(0, (stop.endedAt ?? now) - stop.startedAt);
}

export function isStopLive(stop: LedgerStop): boolean {
  return stop.outcome === 'running';
}

/** The stage caption to show against a live stop. */
export function currentSegment(stop: LedgerStop): LedgerSegment | null {
  return stop.segments[stop.segments.length - 1] ?? null;
}
